/**
 * Utility functions for In-Folder Scheduling (Gantt timeline logic).
 *
 * Run tests with: yarn test inFolderScheduling.test.js
 */

/**
 * Convert a Date to a YYYY-MM-DD string (local midnight).
 * @param {Date} d
 * @returns {string}
 */
export const toDateStr = (d) => {
	const y   = d.getFullYear();
	const m   = String(d.getMonth() + 1).padStart(2, '0');
	const day = String(d.getDate()).padStart(2, '0');
	return `${y}-${m}-${day}`;
};

/**
 * Parse a YYYY-MM-DD string to a local Date at midnight.
 * Returns null when str is falsy.
 * @param {string|null|undefined} str
 * @returns {Date|null}
 */
export const parseDate = (str) => {
	if (!str) return null;
	const [yStr, mStr, dStr] = str.split('-');
	const y = Number(yStr);
	const m = Number(mStr);
	const d = Number(dStr);
	if (!Number.isInteger(y) || !Number.isInteger(m) || !Number.isInteger(d)) return null;
	const dt = new Date(y, m - 1, d);
	if (!Number.isFinite(dt.getTime())) return null;
	if (dt.getFullYear() !== y || dt.getMonth() !== (m - 1) || dt.getDate() !== d) return null;
	return dt;
};

/**
 * Return a new Date that is `n` days after `d` (or before when n is negative).
 * @param {Date} d
 * @param {number} n
 * @returns {Date}
 */
export const addDays = (d, n) => {
	const r = new Date(d);
	r.setDate(r.getDate() + n);
	return r;
};

/**
 * Whole-day difference between two Date values, computed in UTC to avoid
 * DST-transition errors (±1 day on 23/25-hour days).
 * @param {Date} a
 * @param {Date} b
 * @returns {number}
 */
export const diffDays = (a, b) => {
	const utcA = Date.UTC(a.getFullYear(), a.getMonth(), a.getDate());
	const utcB = Date.UTC(b.getFullYear(), b.getMonth(), b.getDate());
	return Math.round((utcB - utcA) / 86400000);
};

/**
 * Format a date range as "M/D – M/D" for use as a Gantt-bar label.
 * Returns an empty string when either date is invalid.
 * @param {string} start  YYYY-MM-DD
 * @param {string} end    YYYY-MM-DD
 * @returns {string}
 */
export const formatBar = (start, end) => {
	const s = parseDate(start);
	const e = parseDate(end);
	if (!s || !e) return '';
	const fmt = (dt) => `${dt.getMonth() + 1}/${dt.getDate()}`;
	return `${fmt(s)} – ${fmt(e)}`;
};

/**
 * Returns true when the given end date is strictly before today (expired).
 * @param {string|null|undefined} end  YYYY-MM-DD
 * @returns {boolean}
 */
export const isExpiredRange = (end) => {
	if (!end) return false;
	const today = toDateStr(new Date());
	return end < today;
};

const VIDEO_EXTS = new Set(['mp4', 'mov', 'avi', 'mkv', 'webm', 'wmv', 'flv', 'm4v', 'mpg', 'mpeg']);
const IMAGE_EXTS = new Set(['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg', 'tiff', 'tif', 'heic', 'avif']);

/**
 * Derive a display type ('video' | 'image' | 'other') from a gallery file object.
 * Priority order: mime_type → duration field → file extension.
 * @param {{ mime_type?: string, duration?: number, file_name?: string, path?: string }} file
 * @returns {'video'|'image'|'other'}
 */
export const getFileType = (file) => {
	const mime = (file.mime_type || '').toLowerCase();
	if (mime.startsWith('video/')) return 'video';
	if (mime.startsWith('image/')) return 'image';
	if (file.duration > 0) return 'video';
	const ext = ((file.file_name || file.path || '').match(/\.([a-z0-9]{2,5})$/i) || [])[1]?.toLowerCase() || '';
	if (VIDEO_EXTS.has(ext)) return 'video';
	if (IMAGE_EXTS.has(ext)) return 'image';
	return 'other';
};

/**
 * Returns true when two date-range objects overlap (inclusive on both ends).
 * @param {{ start_date: string, end_date: string }} a
 * @param {{ start_date: string, end_date: string }} b
 * @returns {boolean}
 */
export const rangesOverlap = (a, b) => a.start_date <= b.end_date && a.end_date >= b.start_date;

/**
 * Returns true when `testRange` would overlap any existing range in `ranges`,
 * excluding the range at `excludeIdx` (used during drag/resize to ignore the
 * bar being moved).
 * @param {Array<{ start_date: string, end_date: string }>} ranges
 * @param {{ start_date: string, end_date: string }} testRange
 * @param {number} excludeIdx  Pass -1 to compare against all ranges.
 * @returns {boolean}
 */
export const wouldOverlap = (ranges, testRange, excludeIdx) =>
	ranges.some((r, i) => i !== excludeIdx && rangesOverlap(testRange, r));
